import React, { useState } from 'react';
import './HeightInput.css';

interface HeightInputProps {
  valueCm?: number;
  valueFeet?: number;
  valueInches?: number;
  onChange: (data: { cm?: number; feet?: number; inches?: number }) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

const HeightInput: React.FC<HeightInputProps> = ({
  valueCm,
  valueFeet,
  valueInches,
  onChange,
  error,
  required = true,
  disabled = false,
}) => {
  const [unit, setUnit] = useState<'cm' | 'ft'>(
    valueCm ? 'cm' : (valueFeet !== undefined ? 'ft' : 'cm')
  );

  const handleUnitToggle = (newUnit: 'cm' | 'ft') => {
    setUnit(newUnit);
    if (newUnit === 'cm') {
      onChange({ cm: valueCm || 0, feet: undefined, inches: undefined });
    } else {
      onChange({ cm: undefined, feet: valueFeet || 0, inches: valueInches || 0 });
    }
  };

  return (
    <div className="form-question">
      <label className="question-label">
        Height {required && <span className="required">*</span>}
      </label>

      {/* Height Unit Toggle */}
      <div className="toggle-switch" role="radiogroup" aria-label="Height unit">
        <button
          type="button"
          className={`toggle-option ${unit === 'cm' ? 'active' : ''}`}
          onClick={() => handleUnitToggle('cm')}
          aria-pressed={unit === 'cm'}
          disabled={disabled}
        >
          cm
        </button>
        <button
          type="button"
          className={`toggle-option ${unit === 'ft' ? 'active' : ''}`}
          onClick={() => handleUnitToggle('ft')}
          aria-pressed={unit === 'ft'}
          disabled={disabled}
        >
          ft-in
        </button>
      </div>

      {/* Height Input: cm */}
      {unit === 'cm' && (
        <div className="height-input-group">
          <input
            type="number"
            id="height-cm"
            name="height_cm"
            className={`form-input number-input ${error ? 'error' : ''}`}
            value={valueCm || ''}
            onChange={(e) => onChange({ cm: parseFloat(e.target.value) || 0 })}
            required={required}
            min="140"
            max="220"
            placeholder="Enter height in cm"
            disabled={disabled}
            aria-describedby={error ? "height-error" : undefined}
          />
        </div>
      )}

      {/* Height Input: ft-in */}
      {unit === 'ft' && (
        <div className="height-input-group">
          <div className="input-row">
            <div className="input-col">
              <input
                type="number"
                id="height-feet"
                name="height_feet"
                className={`form-input number-input ${error ? 'error' : ''}`}
                value={valueFeet || ''}
                onChange={(e) =>
                  onChange({
                    feet: parseInt(e.target.value) || 0,
                    inches: valueInches,
                  })
                }
                required={required}
                min="4"
                max="7"
                placeholder="Feet"
                disabled={disabled}
              />
              <label htmlFor="height-feet" className="input-label-small">
                feet
              </label>
            </div>
            <div className="input-col">
              <input
                type="number"
                id="height-inches"
                name="height_inches"
                className={`form-input number-input ${error ? 'error' : ''}`}
                value={valueInches || ''}
                onChange={(e) =>
                  onChange({
                    feet: valueFeet,
                    inches: parseInt(e.target.value) || 0,
                  })
                }
                required={required}
                min="0"
                max="11"
                placeholder="Inches"
                disabled={disabled}
              />
              <label htmlFor="height-inches" className="input-label-small">
                inches
              </label>
            </div>
          </div>
        </div>
      )}

      {error && (
        <span className="error-message" id="height-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default HeightInput;




